// Image preloading utility to avoid duplicate preloading across components
import { logger } from './logger';

class ImagePreloader {
  constructor() {
    this.cache = new Map();
    this.promises = new Map();
  }

  /**
   * Preload an image and cache the result
   * @param {string} src - Image source URL
   * @param {Object} options - Preload options
   * @returns {Promise<HTMLImageElement>} - Promise that resolves with the loaded image
   */
  async preload(src, options = {}) {
    // Return cached image if already loaded
    if (this.cache.has(src)) {
      logger.debug(`✅ Image already cached: ${src}`);
      return this.cache.get(src);
    }

    // Return existing promise if already preloading
    if (this.promises.has(src)) {
      logger.debug(`⏳ Image already preloading: ${src}`);
      return this.promises.get(src);
    }

    // Create new preload promise
    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        logger.debug(`✅ Image preloaded successfully: ${src}`);
        this.cache.set(src, img);
        this.promises.delete(src);
        resolve(img);
      };
      
      img.onerror = (error) => {
        console.error(`❌ Failed to preload image: ${src}`, error);
        this.promises.delete(src);
        reject(error);
      };

      // Set crossOrigin only if specified
      if (options.crossOrigin) {
        img.crossOrigin = options.crossOrigin;
      }

      img.src = src;
    });

    this.promises.set(src, promise);
    return promise;
  }

  /**
   * Check if an image is already cached
   * @param {string} src - Image source URL
   * @returns {boolean} - True if image is cached
   */
  isCached(src) {
    return this.cache.has(src);
  }

  /**
   * Get cached image if available
   * @param {string} src - Image source URL
   * @returns {HTMLImageElement|null} - Cached image or null
   */
  getCached(src) {
    return this.cache.get(src) || null;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    this.promises.clear();
  }
}

// Create singleton instance
const imagePreloader = new ImagePreloader();

export default imagePreloader;
